import React from 'react';
import SparkleIcon from '../CustomIcons/SparkleIcon';
import Typography from '../Typography';

export interface ChatHeaderProps {
    /** Names the surface, e.g. "Policy chat". */
    title: string;
    /**
     * Marks the surface as AI-owned: the title takes the assistant violet and a sparkle
     * leads it. Defaults to `'default'`, so a chat that is not assistant-authored does not
     * inherit the treatment.
     */
    tone?: 'default' | 'assistant';
    /** Sits opposite the title — a model name, a token count. */
    trailing?: React.ReactNode;
    /** Rows travelling with the header, e.g. `Chat.Context`. Pinned along with it. */
    children?: React.ReactNode;
    className?: string;
}

const TITLE_ASSISTANT_STYLE: React.CSSProperties = {
    color: 'var(--malachite-assistant-foreground)',
};

/**
 * The pinned top of a chat: a title, whatever belongs opposite it, and any rows that travel
 * with them.
 *
 * Opaque, because the transcript scrolls underneath it and has to disappear rather than
 * show through. The soft shadow below is what keeps that from reading as a rendering fault:
 * without it a message is sliced off at a hard edge. A shadow rather than a border, so the
 * boundary is felt rather than drawn.
 */
const ChatHeader: React.FC<ChatHeaderProps> = ({
    title,
    tone = 'default',
    trailing,
    children,
    className = '',
}) => (
    <div
        // Padding of its own, rather than borrowed from a padded parent: it spans the full
        // width of the column so that the frost reaches both edges, which means nothing
        // outside it is insetting its content any more.
        className={`flex shrink-0 flex-col gap-3 px-4 pb-3 pt-4 ${className}`}
        style={{
            backgroundColor: 'var(--malachite-card-background)',
            boxShadow: '0 4px 6px -4px var(--malachite-card-shadow)',
        }}
    >
        <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1.5">
                {tone === 'assistant' ? (
                    <span style={TITLE_ASSISTANT_STYLE} className="flex items-center">
                        <SparkleIcon size={14} />
                    </span>
                ) : null}
                <Typography.Text
                    size="small"
                    fontWeight="semi-bold"
                    {...(tone === 'assistant' && { style: TITLE_ASSISTANT_STYLE })}
                >
                    {title}
                </Typography.Text>
            </span>
            {trailing === undefined ? null : (
                <Typography.SecondaryText size="xsmall">{trailing}</Typography.SecondaryText>
            )}
        </div>
        {children}
    </div>
);

export default ChatHeader;
export { ChatHeader };
